import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from "react";
import { Session, User } from "@supabase/supabase-js";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Sentry, sentryEnabled } from "@/lib/sentry";
import {
  checkPasswordPwned,
  clearForcedResetFlag,
  getStoredForcedResetUserId,
  readForcedResetFlag,
  writeForcedResetFlag,
} from "@/lib/hibp";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  /**
   * True when the user signed in with a password that HIBP flagged as
   * compromised. The app must force them through /password-reset-required
   * before allowing any further activity.
   */
  mustResetPassword: boolean;
  /** Clear the forced-reset flag after a successful password change. */
  clearMustResetPassword: () => void;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Pages where a fresh email-verification sign-in should redirect to /dashboard
const PUBLIC_PATHS = ["/", "/login", "/get-started"];

/**
 * If sessionStorage has a forced-reset flag for someone other than the
 * currently-authenticated user, drop it. This prevents stale entries from
 * a prior session on the same tab from bleeding into the new session.
 */
function evictStaleForcedResetFlag(currentUserId: string | null | undefined) {
  const stored = getStoredForcedResetUserId();
  if (stored && stored !== currentUserId) {
    clearForcedResetFlag();
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mustResetPassword, setMustResetPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Tracks the current authoritative user id so the async HIBP resolver can
  // confirm the session hasn't changed underneath it before writing the flag.
  const currentUserIdRef = useRef<string | null>(null);
  // AbortController for the most recent in-flight HIBP check, so signOut or a
  // subsequent signIn can cancel a stale check before it flips state.
  const hibpAbortRef = useRef<AbortController | null>(null);

  const cancelPendingHibp = useCallback(() => {
    if (hibpAbortRef.current) {
      hibpAbortRef.current.abort();
      hibpAbortRef.current = null;
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      const nextUserId = session?.user?.id ?? null;
      currentUserIdRef.current = nextUserId;

      // Drop any flag left over from a different user on this tab, then
      // rehydrate the flag for the current user (if there is one).
      evictStaleForcedResetFlag(nextUserId);
      setMustResetPassword(readForcedResetFlag(nextUserId));

      if (sentryEnabled) {
        if (session?.user) {
          Sentry.setUser({ id: session.user.id, email: session.user.email });
        } else {
          Sentry.setUser(null);
        }
      }

      // Record last sign-in time
      if (_event === "SIGNED_IN" && session?.user) {
        supabase
          .from("profiles")
          .update({ last_sign_in_at: new Date().toISOString() })
          .eq("user_id", session.user.id)
          .then(() => {});

        // Redirect to dashboard when user arrives from email verification link
        // (they land on a public page with a confirmation token in the URL)
        if (PUBLIC_PATHS.includes(location.pathname)) {
          navigate("/dashboard", { replace: true });
        }
      }

      if (_event === "SIGNED_OUT") {
        cancelPendingHibp();
        clearForcedResetFlag();
        setMustResetPassword(false);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      const nextUserId = session?.user?.id ?? null;
      currentUserIdRef.current = nextUserId;
      evictStaleForcedResetFlag(nextUserId);
      setMustResetPassword(readForcedResetFlag(nextUserId));
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      cancelPendingHibp();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/dashboard` },
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    // Cancel any HIBP check still running from a previous sign-in attempt so
    // it can't race and flip mustResetPassword after this call resolves.
    cancelPendingHibp();

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error || !data.session?.user) {
      // Supabase rejected the credentials — make sure no stale forced-reset
      // flag from a previous session lingers on this tab.
      clearForcedResetFlag();
      setMustResetPassword(false);
      return { error: error as Error | null };
    }

    // Fire-and-forget HIBP check. If the password appears in a breach corpus
    // we flip the forced-reset flag so the route gate can redirect the user.
    const userId = data.session.user.id;
    // Treat a successful signInWithPassword as authoritative for the current
    // user id — onAuthStateChange will confirm this shortly, but recording it
    // now closes the race where HIBP resolves before the event fires.
    currentUserIdRef.current = userId;
    const controller = new AbortController();
    hibpAbortRef.current = controller;

    checkPasswordPwned(password, { signal: controller.signal })
      .then((result) => {
        // Only commit the flag if this check wasn't superseded AND the
        // session still belongs to the same user we started the check for.
        if (controller.signal.aborted) return;
        if (currentUserIdRef.current !== userId) return;
        if (result.compromised) {
          writeForcedResetFlag(userId);
          setMustResetPassword(true);
        }
      })
      .catch(() => {
        /* fail open — do not block sign-in on network errors or aborts */
      })
      .finally(() => {
        if (hibpAbortRef.current === controller) {
          hibpAbortRef.current = null;
        }
      });

    return { error: null };
  };

  const signOut = async () => {
    cancelPendingHibp();
    clearForcedResetFlag();
    setMustResetPassword(false);
    currentUserIdRef.current = null;
    await supabase.auth.signOut();
  };

  const clearMustResetPassword = useCallback(() => {
    clearForcedResetFlag();
    setMustResetPassword(false);
  }, []);


  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        mustResetPassword,
        clearMustResetPassword,
        signUp,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
